import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Modal, Table, Radio, Alert, Space, Typography } from 'antd';
import { ReferenceDataVisualStore } from '../../stores';
import type { ImportedRefDataTable } from '../../services/ReferenceDataService';

const { Text } = Typography;

type Props = {
    store: ReferenceDataVisualStore;
};

const ImportRefDataTablesModal: React.FC<Props> = ({ store }) => {
    const onCancel = () => {
        store.setImportDialogVisible(false);
        store.resetImportData();
    };

    const onSubmit = () => {
        store.commitImportedTables();
    };

    const importData = store.importData;
    if (!importData) {
        return null;
    }

    const hasConflicts = importData.importedData.tables.some(table =>
        importData.existingTableNames.includes(table.tableName)
    );

    const columns = [
        {
            title: 'Table Name',
            dataIndex: 'tableName',
            key: 'tableName',
            width: 250,
            render: (tableName: string) => <Text strong>{tableName}</Text>
        },
        {
            title: 'Records',
            dataIndex: 'recordCount',
            key: 'recordCount',
            width: 100,
            render: (_: unknown, record: ImportedRefDataTable) => record.recordCount
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 150,
            render: (_: unknown, record: ImportedRefDataTable) => {
                const exists = importData.existingTableNames.includes(record.tableName);
                return <Text type={exists ? 'warning' : 'success'}>{exists ? 'Already exists' : 'New table'}</Text>;
            }
        },
        {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            render: (_: unknown, record: ImportedRefDataTable) => {
                const exists = importData.existingTableNames.includes(record.tableName);

                if (!exists) {
                    return <Text type="secondary">Will be created</Text>;
                }

                return (
                    <Radio.Group
                        value={record.conflictResolution}
                        onChange={e => store.setTableConflictResolution(record.tableName, e.target.value)}
                        size="small"
                    >
                        <Space direction="vertical">
                            <Radio value="overwrite">Overwrite existing table</Radio>
                            <Radio value="copy">{`Create copy as: Copy_1_of_${record.tableName}`}</Radio>
                        </Space>
                    </Radio.Group>
                );
            }
        }
    ];

    const totalRecords = importData.importedData.tables.reduce((sum, table) => sum + (table.recordCount || 0), 0);

    return (
        <Modal
            title="Import Reference Data Tables"
            visible={store.importDialogVisible}
            onCancel={onCancel}
            onOk={onSubmit}
            width={900}
            okText="Import"
            cancelText="Cancel"
            confirmLoading={store.isLoading}
        >
            <Space direction="vertical" style={{ width: '100%' }} size="large">
                <Alert
                    message="Import Summary"
                    description={
                        <div>
                            <div>
                                <strong>{importData.importedData.tables.length}</strong> table(s) will be imported with{' '}
                                <strong>{totalRecords}</strong> total records
                            </div>
                            {hasConflicts && (
                                <div style={{ marginTop: 8 }}>
                                    <Text type="warning">
                                        Some tables already exist. Please choose how to handle conflicts below.
                                    </Text>
                                </div>
                            )}
                        </div>
                    }
                    type="info"
                    showIcon
                />

                <Table
                    columns={columns}
                    dataSource={importData.importedData.tables}
                    rowKey="tableName"
                    pagination={false}
                    size="small"
                />
            </Space>
        </Modal>
    );
};

export default observer(ImportRefDataTablesModal);
